import { createSlice } from "@reduxjs/toolkit";
import { showNotification } from "./notificationSlice";
import { factory } from "../../api/apiFactory";
import { JOrder } from "./jomla/orderSlice";
import { downloadFile, getExportErrorMessage } from "../../helpers/exportFile";
const posApi = factory.get("pos");

// The document images live on their own multipart endpoint (`pos-image/{id}`),
// so they travel outside the JSON create/update payloads.
export const POS_IMAGE_FIELDS = [
  "front_id",
  "back_id",
  "living_front",
  "living_back",
  "owner_image",
  "owner_inside_image",
  "owner_outside_image",
  "signature",
  "finger_print",
  "other_first_image",
  "other_second_image",
];

const initialState = {
  searchBody: {
    search: "",
    rep_ids: null,
    group_id: null,
    startDate: null,
    endDate: null,
    custom_discounts: 0,
    has_j_balance: null,
    has_e_payment: null,
    is_approved: "1",
    min_order_amount: "",
    province_id: null,
    pos_type_ids: null,
    pos_field_ids: null,
    city_ids: null,
    lock_after_payment: null,
    is_less_than_min_balance: null,
  },
  pos: {
    loading: false,
    dialog: false,
    form: {
      id: "",
      user_id: "",
      password: "",
      email: "",
      name: "",
      owner: "",
      phone: "",
      group_id: null,
      can_transfer: false,
      province_id: null,
      city_id: null,
      wage: "",
      max_daily_transfer: 1000000,
      max_monthly_transfer: 5000000,
      active_user: false,
      active_wallet: false,
      max_balance: 10000000,
      max_debt: 250000,
      min_balance: 50000,
      daily_transfer_balance: "",
      has_card_recharge: false,
      has_card_issuing: false,
      has_external_orders: false,
      has_e_payment: false,
      sim_card_number: "",
      pos_type_id: null,
      pos_field_id: null,
      j_group_id: null,
      j_rep_id: null,
      other_id: "",
      lock_after_payment: false,
      e_payment_rep_id: null,
      front_id: null,
      back_id: null,
      living_front: null,
      living_back: null,
      owner_image: null,
      owner_inside_image: null,
      owner_outside_image: null,
      signature: null,
      finger_print: null,
      other_first_image: null,
      other_second_image: null,
    },
  },
  poses: {
    loading: false,
    data: [],
    total: 0,
  },
  posesList: {
    loading: false,
    data: [],
    total: 0,
  },
  lastOtp: {
    dialog: false,
    data: [],
  },
  mapData: {
    data: [],
    dialog: false,
  },
  posLocations: {
    data: [],
    dialog: false,
    loading: false,
    pos: null,
  },
};

export const posSlice = createSlice({
  name: "pos",
  initialState,
  reducers: {
    setDialog: (state, action) => {
      state.pos.dialog = !state.pos.dialog;
    },
    setDataTable: (state, { payload }) => {
      state.pos.data = payload.data.data;
      state.pos.total = payload.data.total;
    },
    setDataList: (state, { payload }) => {
      state.posesList.data = payload.data.data;
    },
    setLastOptDialog: (state, action) => {
      state.lastOtp.dialog = !state.lastOtp.dialog;
    },
    setLastOpt: (state, { payload }) => {
      state.lastOtp.data = payload;
    },
    setById: (state, { payload }) => {
      // state.pos.form = payload
      state.pos.form.id = payload.id;
      state.pos.form.name = payload.name;
      state.pos.form.password = payload.password;
      state.pos.form.email = payload.email;
      state.pos.form.active_user = payload.active_user;
      state.pos.form.active_wallet = payload.active_wallet;
      state.pos.form.address = payload.address;
      state.pos.form.owner = payload.owner;
      state.pos.form.mobile = payload.mobile;
      state.pos.form.group_id = payload.group;
      state.pos.form.rep_id = payload.representative;
      state.pos.form.can_transfer = payload.can_transfer;
      state.pos.form.province_id = payload.province;
      state.pos.form.city_id = payload.city;
      state.pos.form.wage = payload.wage;
      state.pos.form.max_daily_transfer = payload.max_daily_transfer;
      state.pos.form.max_monthly_transfer = payload.max_monthly_transfer;
      state.pos.form.user = payload.user;
      state.pos.form.max_debt = payload.wallet.max_debt;
      state.pos.form.max_balance = payload.wallet.max_balance;
      state.pos.form.min_balance = payload.wallet.min_balance ?? 50000;
      state.pos.form.daily_transfer_balance = payload.daily_transfer_balance;
      state.pos.form.is_approved = payload.is_approved;
      state.pos.form.latitude = payload.latitude;
      state.pos.form.longitude = payload.longitude;
      state.pos.form.has_card_issuing = payload.has_card_issuing;
      state.pos.form.has_card_recharge = payload.has_card_recharge;
      state.pos.form.has_external_orders = payload.has_external_orders;
      state.pos.form.has_e_payment = payload.has_e_payment;
      state.pos.form.sim_card_number = payload.sim_card_number;
      state.pos.form.pos_type_id = payload.pos_type_id;
      state.pos.form.pos_field_id = payload.pos_field_id;
      state.pos.form.j_group_id = payload.j_group_id;
      state.pos.form.j_rep_id = payload.j_rep_id;
      state.pos.form.other_id = payload.other_id ?? "";
      state.pos.form.lock_after_payment = payload.lock_after_payment;
      state.pos.form.e_payment_rep_id = {
        id: payload.e_payment_rep_id,
        name: payload.e_payment_rep,
      };
      POS_IMAGE_FIELDS.forEach((key) => {
        state.pos.form[key] = payload[key] ?? null;
      });
    },
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setFormImage: (state, { payload }) => {
      state.pos.form[payload.name] = payload.value ?? null;
    },
    resetForm: (state, action) => {
      state.pos.form = initialState.pos.form;
    },
    setSearchBody: (state, { payload }) => {
      state.searchBody = payload;
    },
    setMapDataDialog: (state, { payload }) => {
      state.mapData.dialog = !state.mapData.dialog;
      if (payload) {
        state.mapData.data = payload;
      } else {
        state.mapData.data = [];
      }
    },
    openPosLocationsDialog: (state, { payload }) => {
      state.posLocations.dialog = true;
      state.posLocations.pos = payload ?? null;
    },
    closePosLocationsDialog: (state) => {
      state.posLocations.dialog = false;
      state.posLocations.data = [];
      state.posLocations.pos = null;
    },
    setPosLocations: (state, { payload }) => {
      state.posLocations.data = payload || [];
    },
    setPosLocationsLoading: (state, { payload }) => {
      state.posLocations.loading = Boolean(payload);
    },
  },
});

export const {
  setLoading,
  setFormImage,
  setDialog,
  setById,
  setDataTable,
  resetForm,
  setSearchBody,
  setDataList,
  setLastOpt,
  setLastOptDialog,
  setMapDataDialog,
  openPosLocationsDialog,
  closePosLocationsDialog,
  setPosLocations,
  setPosLocationsLoading,
} = posSlice.actions;

export default posSlice.reducer;

//axios
const getAll = (params) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.getAll(params);
    dispatch(setDataTable(res));
    dispatch(setLoading("pos"));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const getAllList = (params) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.getAllList(params);
    dispatch(setDataList(res));
    dispatch(setLoading("pos"));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const deleteById = (id, _SearchBody) => async (dispatch) => {
  try {
    await posApi.delete(id);
    dispatch(
      showNotification({
        message: "تم الحذف بنجاح",
        type: "success",
      }),
    );
    dispatch(getAll(_SearchBody));
  } catch (err) {
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const getById = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.getById(id);
    dispatch(setById(res.data.data[0]));

    dispatch(setDialog());
    dispatch(setLoading("pos"));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const getByUserId = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.getByUserId(id);
    dispatch(setById(res.data.data[0]));

    dispatch(setDialog());
    dispatch(setLoading("pos"));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const create = (data, searchBody, images) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.create(data);
    // The documents endpoint is keyed by the POS id, which only exists once the
    // POS itself is created, so the upload has to follow the create call.
    let documentsSkipped = false;
    if (images) {
      const createdId =
        res?.data?.data?.[0]?.id ?? res?.data?.data?.id ?? res?.data?.id;
      if (createdId) {
        await posApi.uploadImages(createdId, images);
      } else {
        documentsSkipped = true;
      }
    }
    dispatch(
      showNotification({
        message: documentsSkipped
          ? "تمت الاضافة بنجاح لكن تعذر رفع المستمسكات، يرجى رفعها من خلال التعديل"
          : "تمت الاضافة  بنجاح",
        type: documentsSkipped ? "warning" : "success",
      }),
    );
    dispatch(setLoading("pos"));
    dispatch(setDialog());
    dispatch(getAll(searchBody));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const update =
  (
    id,
    data,
    _SearchBody,
    isNeedReload,
    from,
    jOrderSearchBody,
    jOrderId,
    images,
  ) =>
  async (dispatch) => {
    try {
      dispatch(setLoading("pos"));
      await posApi.update(id, data);
      if (images) {
        await posApi.uploadImages(id, images);
      }
      dispatch(
        showNotification({
          message: "تم التعديل بنجاح",
          type: "success",
        }),
      );
      dispatch(setLoading("pos"));
      dispatch(setDialog());
      if (isNeedReload) {
        dispatch(getAll(_SearchBody));
      }
      if (from === "JOrderDetails") {
        dispatch(JOrder.getOrderDetails(jOrderId));
      }
      if (from === "JOrders") {
        dispatch(JOrder.getAll(jOrderSearchBody));
      }
    } catch (err) {
      dispatch(setLoading("pos"));
      dispatch(
        showNotification({
          message: err.response.data.errors[0],
          type: "error",
        }),
      );
      throw new Error(err);
    }
  };

// Clears one stored document. The form keeps rendering from `pos.form`, so the
// slot empties as soon as the server confirms the delete.
const deleteImage = (id, name) => async (dispatch) => {
  try {
    await posApi.deleteImage(id, name);
    dispatch(setFormImage({ name, value: null }));
    dispatch(
      showNotification({
        message: "تم حذف المستمسك بنجاح",
        type: "success",
      }),
    );
  } catch (err) {
    dispatch(
      showNotification({
        message: err?.response?.data?.errors?.[0] || "تعذر حذف المستمسك",
        type: "error",
      }),
    );
    throw new Error(err);
  }
};

const updateEPaymentFees = (id, data, _SearchBody) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    await posApi.ePaymentFees(id, data);
    dispatch(
      showNotification({
        message: "تم تحديث عمولة الدفع الإلكتروني بنجاح",
        type: "success",
      }),
    );
    dispatch(setLoading("pos"));
    dispatch(getAll(_SearchBody));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};

const exportExcel = (params) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.export({
      ...params,
      is_excel: 1,
    });
    await downloadFile(res, `pos.xlsx`);
    dispatch(setLoading("pos"));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: await getExportErrorMessage(err),
        type: "error",
      }),
    );
    throw new Error(err);
  }
};
const getLastOtp = (id) => async (dispatch) => {
  try {
    dispatch(setLoading("pos"));
    const res = await posApi.getLastOpt(id);
    dispatch(setLastOpt(res.data.data[0]));
    dispatch(setLastOptDialog());
    dispatch(setLoading("pos"));
  } catch (err) {
    dispatch(setLoading("pos"));
    dispatch(
      showNotification({
        message: err.response.data.errors[0],
        type: "error",
      }),
    );
    throw new Error(err);
  }
};

// Fetch the devices' recorded locations for a single POS. They arrive on the
// get-by-id payload under `pos_locations`, so no dedicated endpoint is needed.
const getPosLocations = (id, pos) => async (dispatch) => {
  try {
    dispatch(openPosLocationsDialog(pos ?? null));
    dispatch(setPosLocationsLoading(true));
    dispatch(setPosLocations([]));

    const res = await posApi.getById(id);
    dispatch(setPosLocations(res.data.data[0]?.pos_locations ?? []));
    dispatch(setPosLocationsLoading(false));
  } catch (err) {
    dispatch(setPosLocationsLoading(false));
    dispatch(closePosLocationsDialog());
    dispatch(
      showNotification({
        message: err?.response?.data?.errors?.[0] || "حدث خطأ غير متوقع",
        type: "error",
      }),
    );
    throw new Error(err);
  }
};

export const PointOfSales = {
  getAll,
  exportExcel,
  deleteById,
  getById,
  create,
  update,
  getAllList,
  getLastOtp,
  getByUserId,
  updateEPaymentFees,
  getPosLocations,
  deleteImage,
};
